import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import type { DatasetResult } from '@/types/dashboard';

interface DatasetBarChartProps {
  datasets: DatasetResult[];
  aggregateF1?: number;
}

const COLORS = {
  precision: '#f97316', // orange
  recall: '#22d3ee', // cyan
  f1: '#a78bfa', // violet
};

function shortenName(name: string): string {
  return name
    .replace(/Venues? with /i, '')
    .replace(/ in NL$/i, '')
    .replace(/ Options/i, '')
    .replace(/Canal-side /i, '')
    .replace(/Waterfront \/ /, 'Waterfront ')
    .replace(/ Venues/i, '');
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
    payload: { fullName: string; positives: number; negatives: number };
  }>;
  label?: string;
}

function CustomTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <div className="rounded-lg border border-primary/30 bg-card/95 backdrop-blur-md p-3 shadow-xl">
      <div className="font-semibold text-sm text-foreground mb-1.5">{data.fullName}</div>
      <div className="text-[10px] text-muted-foreground mb-2">
        {data.positives}P / {data.negatives}N
      </div>
      <div className="space-y-1">
        {payload.map((p) => (
          <div key={p.name} className="flex items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
              <span className="text-muted-foreground">{p.name}</span>
            </div>
            <span className="font-bold tabular-nums" style={{ color: p.color }}>
              {p.value.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DatasetBarChart({ datasets, aggregateF1 }: DatasetBarChartProps) {
  const chartData = useMemo(() => {
    return datasets
      .filter((d) => d.status === 'completed' && d.phase2)
      .map((d) => ({
        name: shortenName(d.dataset_name),
        fullName: d.dataset_name,
        precision: d.phase2!.precision,
        recall: d.phase2!.recall,
        f1: d.phase2!.f1,
        positives: d.total_positives ?? 0,
        negatives: d.total_negatives ?? 0,
      }))
      .sort((a, b) => a.f1 - b.f1);
  }, [datasets]);

  if (chartData.length === 0) return null;

  return (
    <div className="rounded-xl border border-primary/20 bg-card/50 p-5 backdrop-blur-xs">
      <h3 className="text-base font-bold bg-linear-to-r from-primary to-orange-400 bg-clip-text text-transparent mb-4">
        Per-Dataset Metrics
      </h3>
      <ResponsiveContainer width="100%" height={Math.max(280, chartData.length * 44 + 60)}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 20, bottom: 0, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--primary) / 0.08)" horizontal={false} />
          <XAxis
            type="number"
            domain={[0, 100]}
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
            tickFormatter={(v: number) => `${v}%`}
            axisLine={{ stroke: 'hsl(var(--primary) / 0.15)' }}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={120}
            tick={{ fill: 'hsl(var(--foreground))', fontSize: 12, fontWeight: 500 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--primary) / 0.05)' }} />
          <Legend
            wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
            formatter={(value: string) => <span style={{ color: 'hsl(var(--muted-foreground))' }}>{value}</span>}
          />
          {aggregateF1 != null && (
            <ReferenceLine
              x={aggregateF1}
              stroke="hsl(var(--primary) / 0.4)"
              strokeDasharray="6 4"
              label={{
                value: `Avg F1: ${aggregateF1.toFixed(1)}%`,
                position: 'top',
                fill: 'hsl(var(--primary))',
                fontSize: 11,
                fontWeight: 600,
              }}
            />
          )}
          <Bar dataKey="precision" name="Precision" fill={COLORS.precision} radius={[0, 3, 3, 0]} barSize={10}>
            {chartData.map((_, i) => (
              <Cell key={i} fillOpacity={0.85} />
            ))}
          </Bar>
          <Bar dataKey="recall" name="Recall" fill={COLORS.recall} radius={[0, 3, 3, 0]} barSize={10}>
            {chartData.map((_, i) => (
              <Cell key={i} fillOpacity={0.85} />
            ))}
          </Bar>
          <Bar dataKey="f1" name="F1" fill={COLORS.f1} radius={[0, 3, 3, 0]} barSize={10}>
            {chartData.map((_, i) => (
              <Cell key={i} fillOpacity={0.85} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
